import { createFileRoute, Navigate, useNavigate } from "@tanstack/react-router";
import { Box, Button, EmptyState, Separator, useIsMobile } from "@salestrak/ui";

import { PageHeader } from "#app/components/page-header";
import { useWorkspace } from "#app/lib/data/workspace-context";
import { routePath } from "#app/lib/hooks/use-app-navigate";

// TanStack has no built-in Unmatched screen — render a minimal not-found body
// with one way back: the workspace's home.
function Unmatched({ workspace }: { workspace: string }) {
  const navigate = useNavigate();
  return (
    <Box fill="flex" align="center" justify="center" padding="lg">
      <EmptyState
        actions={
          <Button onClick={() => navigate({ to: routePath(`/${workspace}`) })}>
            Go to Home
          </Button>
        }
        description="The link may be wrong, or the page may have moved."
        icon="search"
        media="icon"
        title="This page could not be found"
      />
    </Box>
  );
}

// Catch-all for any path under a workspace that didn't match a real route.
// Its only job is case-recovery: route *names* are case-sensitive, so
// `/sales/Map` 404s even though `/sales/map` exists. We re-case each segment
// that matches a known static route name and redirect to the canonical path;
// genuinely unknown paths fall through to the normal Unmatched screen.
//
// Critical: dynamic id segments ($workflowId, $apiKeyId, $teamId, …) are
// case-sensitive and must survive untouched — `/sales/workflow/AbC123/Board`
// must keep `AbC123` and only fix `Board`→`board`. We achieve that by
// normalizing ONLY segments whose lowercase form is in STATIC_SEGMENTS; an
// id never appears here, so it's passed through byte-for-byte. (Omitting a
// route name here just means that one route won't be case-corrected — never
// an id corruption. Add new static route segments as routes are added.)
const STATIC_SEGMENTS = new Set([
  // top-level workspace sections
  "dashboards",
  "map",
  "inbox",
  "members",
  "teams",
  "org-chart",
  "settings",
  "shared",
  "workflow",
  "workflows",
  "team",
  "member",
  "my-work",
  // workflow/$workflowId + team/$teamId view tabs
  "overview",
  "board",
  "list",
  "chart",
  "dashboard",
  "view",
  // dashboards/my
  "my",
  // settings subtree
  "access",
  "api",
  "keys",
  "webhooks",
  "appearance",
  "custom-fields",
  "issue-sources",
  "issue-types",
  "outcomes",
  "permissions",
  "preferences",
  "profile",
  "trash",
  "updates",
  "workspace",
  // shared leaf actions
  "create",
  "edit",
]);

function WorkspaceCatchAll() {
  const { workspace, _splat } = Route.useParams();
  const isMobile = useIsMobile();
  const { activeWorkspace } = useWorkspace();
  const rest = (_splat ?? "").split("/").filter(Boolean);

  const normalized = rest.map((seg) => {
    const lower = seg.toLowerCase();
    return STATIC_SEGMENTS.has(lower) ? lower : seg;
  });

  const changed = normalized.some((seg, i) => seg !== rest[i]);

  if (changed) {
    // Re-attach the query string explicitly — the map route depends on
    // ?lat&lng&z.
    const search = typeof window !== "undefined" ? window.location.search : "";
    const path = [workspace, ...normalized].join("/");
    return <Navigate to={routePath(`/${path}${search}`)} />;
  }

  // Desktop keeps the bare Unmatched screen — the rail and module menu stay
  // visible alongside it.
  if (!isMobile) return <Unmatched workspace={workspace} />;

  // Mobile: the drawer is the only path back to navigation and Unmatched
  // renders no chrome, so the user would be stranded. Give it the Home-style
  // header with the workspace name — its breadcrumb carries the mobile nav
  // trigger that reopens the drawer.
  return (
    <Box direction="column" fill="flex">
      <PageHeader title={activeWorkspace?.name ?? "Workspace"} />
      <Separator />
      <Unmatched workspace={workspace} />
    </Box>
  );
}

export const Route = createFileRoute("/(app)/$workspace/$")({
  component: WorkspaceCatchAll,
});
